import * as THREE from 'three'
import type { CelestialBody } from '@/_types/celestial-body'
import type { PointerHandlers } from '@/_types/scene'

/**
 * Resolves pointer and touch input against the scene. It tracks the normalized
 * pointer for camera parallax, raycasts to find the hovered body, and decides
 * when a body is activated. On mouse, a click enters a body directly; on touch, a
 * tap simply selects it and the info card's button performs the navigation.
 */
export class PointerController {
  private readonly domElement: HTMLElement
  private readonly camera: THREE.Camera
  private readonly resolvePickMeshes: () => THREE.Object3D[]
  private readonly resolveBody: (id: string) => CelestialBody | null
  private readonly handlers: PointerHandlers
  private readonly raycaster: THREE.Raycaster
  private readonly pointer: THREE.Vector2
  private readonly downPosition: THREE.Vector2
  private hoveredId: string | null
  private isEnabled: boolean

  /**
   * @param {HTMLElement} domElement - The element that receives pointer events.
   * @param {THREE.Camera} camera - The camera used for raycasting.
   * @param {() => THREE.Object3D[]} resolvePickMeshes - Supplies current pick meshes.
   * @param {(id: string) => CelestialBody | null} resolveBody - Maps a body id to its data.
   * @param {PointerHandlers} handlers - The hover/activate/move callbacks.
   */
  public constructor(
    domElement: HTMLElement,
    camera: THREE.Camera,
    resolvePickMeshes: () => THREE.Object3D[],
    resolveBody: (id: string) => CelestialBody | null,
    handlers: PointerHandlers,
  ) {
    this.domElement = domElement
    this.camera = camera
    this.resolvePickMeshes = resolvePickMeshes
    this.resolveBody = resolveBody
    this.handlers = handlers
    this.raycaster = new THREE.Raycaster()
    this.pointer = new THREE.Vector2()
    this.downPosition = new THREE.Vector2()
    this.hoveredId = null
    this.isEnabled = true

    this.handlePointerMove = this.handlePointerMove.bind(this)
    this.handlePointerDown = this.handlePointerDown.bind(this)
    this.handlePointerUp = this.handlePointerUp.bind(this)

    this.domElement.addEventListener('pointermove', this.handlePointerMove)
    this.domElement.addEventListener('pointerdown', this.handlePointerDown)
    this.domElement.addEventListener('pointerup', this.handlePointerUp)
  }

  /**
   * Enable or disable input handling (disabled during warp transitions).
   *
   * @param {boolean} isEnabled - True to accept input.
   * @returns {void}
   */
  public setEnabled(isEnabled: boolean): void {
    this.isEnabled = isEnabled
    if (!isEnabled) {
      this.clearHover()
    }
  }

  /**
   * Tear down all event listeners.
   *
   * @returns {void}
   */
  public dispose(): void {
    this.domElement.removeEventListener('pointermove', this.handlePointerMove)
    this.domElement.removeEventListener('pointerdown', this.handlePointerDown)
    this.domElement.removeEventListener('pointerup', this.handlePointerUp)
  }

  /**
   * Track the pointer, drive parallax, and update the hovered body.
   *
   * @param {PointerEvent} event - The pointer move event.
   * @returns {void}
   */
  private handlePointerMove(event: PointerEvent): void {
    const rectangle = this.domElement.getBoundingClientRect()
    const normalizedX = ((event.clientX - rectangle.left) / rectangle.width) * 2 - 1
    const normalizedY = -(((event.clientY - rectangle.top) / rectangle.height) * 2 - 1)
    this.pointer.set(normalizedX, normalizedY)
    this.handlers.onPointerMove(normalizedX, normalizedY)

    if (!this.isEnabled || event.pointerType === 'touch') {
      return
    }
    this.updateHover()
  }

  /**
   * Record where a press began so a tap can be distinguished from a drag.
   *
   * @param {PointerEvent} event - The pointer down event.
   * @returns {void}
   */
  private handlePointerDown(event: PointerEvent): void {
    this.downPosition.set(event.clientX, event.clientY)
  }

  /**
   * On release, decide whether a body was tapped or clicked and act accordingly.
   *
   * @param {PointerEvent} event - The pointer up event.
   * @returns {void}
   */
  private handlePointerUp(event: PointerEvent): void {
    if (!this.isEnabled) {
      return
    }
    const travelled = Math.hypot(event.clientX - this.downPosition.x, event.clientY - this.downPosition.y)
    if (travelled > 12) {
      return
    }

    const rectangle = this.domElement.getBoundingClientRect()
    const normalizedX = ((event.clientX - rectangle.left) / rectangle.width) * 2 - 1
    const normalizedY = -(((event.clientY - rectangle.top) / rectangle.height) * 2 - 1)
    this.pointer.set(normalizedX, normalizedY)

    const body = this.raycastBody()
    if (body === null) {
      if (event.pointerType === 'touch') {
        this.clearHover()
      }
      return
    }

    if (event.pointerType === 'touch') {
      this.setHovered(body.id)
      return
    }
    this.handlers.onActivate(body)
  }

  /**
   * Raycast at the current pointer and update the hovered body if it changed.
   *
   * @returns {void}
   */
  private updateHover(): void {
    const body = this.raycastBody()
    const nextId = body === null ? null : body.id
    if (nextId === this.hoveredId) {
      return
    }
    if (nextId === null) {
      this.clearHover()
      return
    }
    this.setHovered(nextId)
  }

  /**
   * Raycast at the current pointer position and resolve the front-most body.
   *
   * @returns {CelestialBody | null} The hit body, or null.
   */
  private raycastBody(): CelestialBody | null {
    this.raycaster.setFromCamera(this.pointer, this.camera)
    const intersections = this.raycaster.intersectObjects(this.resolvePickMeshes(), false)
    if (intersections.length === 0) {
      return null
    }
    const bodyId = intersections[0].object.userData.bodyId
    if (typeof bodyId !== 'string') {
      return null
    }
    return this.resolveBody(bodyId)
  }

  /**
   * Mark a body as hovered, update the cursor, and notify the handler.
   *
   * @param {string} id - The hovered body id.
   * @returns {void}
   */
  private setHovered(id: string): void {
    this.hoveredId = id
    this.domElement.style.cursor = 'pointer'
    this.handlers.onHover(this.resolveBody(id))
  }

  /**
   * Clear any hover state, reset the cursor, and notify the handler.
   *
   * @returns {void}
   */
  private clearHover(): void {
    if (this.hoveredId === null) {
      this.domElement.style.cursor = 'default'
      return
    }
    this.hoveredId = null
    this.domElement.style.cursor = 'default'
    this.handlers.onHover(null)
  }
}
